import React from 'react';
import styled from 'styled-components';
import {QUOTE_DOC_COLORS as C} from '../pdf/quotePdfStyles';
import {plateTextLeaves} from '../resolver/resolvePlateValue';
import type {PlateBlockNode, PlateInlineNode, PlateTextLeaf, PlateValue} from '../resolver/types';

const H1 = styled.h2`
	/* Same reason as the cover title: a global heading rule would otherwise beat the page's
	   selected font. */
	font-family: inherit;
	font-size: 16px;
	font-weight: 700;
	margin: 0 0 6px;
`;

const P = styled.p`
	font-size: 10px;
	line-height: 1.65;
	margin: 0 0 4px;
	color: ${C.body};
`;

const Ul = styled.ul`
	margin: 0 0 4px;
	padding-left: 16px;
`;

const Ol = styled.ol`
	margin: 0 0 4px;
	padding-left: 16px;
`;

const Li = styled.li`
	font-size: 10px;
	line-height: 1.65;
	color: ${C.body};
`;

const Mention = styled.em`
	font-style: italic;
`;

const Code = styled.code`
	font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
	font-size: 0.9em;
	background: rgba(0, 0, 0, 0.06);
	border-radius: 3px;
	padding: 0 3px;
`;

function renderInline(node: PlateInlineNode, key: number): React.ReactNode {
	if ('type' in node && node.type === 'mention') {
		return <Mention key={key}>{`{${node.value}}`}</Mention>;
	}
	const leaf = node as PlateTextLeaf;
	let content: React.ReactNode = leaf.text;
	if (leaf.code) content = <Code>{content}</Code>;
	if (leaf.bold) content = <strong>{content}</strong>;
	if (leaf.italic) content = <em>{content}</em>;
	if (leaf.underline) content = <u>{content}</u>;
	if (leaf.strikethrough) content = <s>{content}</s>;
	if (content === leaf.text) return leaf.text;
	return <React.Fragment key={key}>{content}</React.Fragment>;
}

const inlineContent = (node: PlateBlockNode): React.ReactNode => plateTextLeaves(node).map(renderInline);

function renderBlock(block: PlateBlockNode, key: number): React.ReactNode {
	switch (block.type) {
		case 'h1':
			return <H1 key={key}>{inlineContent(block)}</H1>;
		case 'ul':
		case 'ol': {
			const List = block.type === 'ul' ? Ul : Ol;
			return (
				<List key={key}>
					{(block.children as PlateBlockNode[]).map((li, j) => (
						<Li key={j}>{inlineContent(li)}</Li>
					))}
				</List>
			);
		}
		case 'li':
			return <Li key={key}>{inlineContent(block)}</Li>;
		case 'p':
		default:
			return <P key={key}>{inlineContent(block)}</P>;
	}
}

/** Renders a Plate rich-text tree as document HTML — the live-preview twin of PlateToPdf. */
const PlateToHtml: React.FC<{value: PlateValue}> = ({value}) => <div>{value.map((block, i) => renderBlock(block, i))}</div>;

export default PlateToHtml;
